import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getApiUrl } from '../config/api.config';
import Logo from '../components/Logo';
import CreateInvestorModal from '../components/CreateInvestorModal';
import AlertDialog from '../components/AlertDialog';
import { fileAPI } from '../lib/api';

const InvestorNetworkInvestorsPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { tenantId } = useParams();
    const tenantPath = tenantId || user?.tenantIdentifier || 'default';

    const [loading, setLoading] = useState(true);
    const [investors, setInvestors] = useState([]);
    const [investments, setInvestments] = useState([]);
    const [startups, setStartups] = useState([]);
    const [showAddInvestorModal, setShowAddInvestorModal] = useState(false);
    const [investorSearchEmail, setInvestorSearchEmail] = useState('');
    const [investorSearchResult, setInvestorSearchResult] = useState(null);
    const [investorSearchLoading, setInvestorSearchLoading] = useState(false);
    const [pendingCreateEmail, setPendingCreateEmail] = useState('');
    const [investorSearchExpanded, setInvestorSearchExpanded] = useState(false);
    const [alert, setAlert] = useState({ isOpen: false, title: '', message: '', type: 'info' });

    const [sidebarExpanded, setSidebarExpanded] = useState(false);
    const sidebarOpen = sidebarExpanded;
    const [networkLogoUrl, setNetworkLogoUrl] = useState(null);
    const [networkLogoLoading, setNetworkLogoLoading] = useState(false);
    const [logoUploading, setLogoUploading] = useState(false);
    const logoInputRef = useRef(null);

    const isAdminMode = user?.role === 'ADMIN' || user?.role === 'TENANT_ADMIN';
    const [investorNetworks, setInvestorNetworks] = useState([]);
    const [selectedNetworkId, setSelectedNetworkId] = useState(null);
    const effectiveNetworkId = isAdminMode ? selectedNetworkId : user?.id;

    useEffect(() => {
        if (isAdminMode) {
            fetch(getApiUrl('admin/users/investor-networks'), { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } })
                .then((r) => (r.ok ? r.json() : []))
                .then((networks) => {
                    setInvestorNetworks(networks || []);
                    if (networks?.length && !selectedNetworkId) setSelectedNetworkId(networks[0].id);
                })
                .catch(() => setInvestorNetworks([]));
        }
    }, [isAdminMode]);

    const loadInvestors = async () => {
        try {
            const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
            if (isAdminMode && effectiveNetworkId) {
                const response = await fetch(getApiUrl(`admin/users/investors?networkId=${effectiveNetworkId}`), { headers });
                if (response.ok) {
                    const list = await response.json();
                    setInvestors(list || []);
                    return list || [];
                }
            } else if (!isAdminMode && user?.id) {
                const response = await fetch(getApiUrl('users/my-network/investors'), { headers });
                if (response.ok) {
                    const data = await response.json();
                    const list = data || [];
                    setInvestors(list);
                    return list;
                }
            }
        } catch (error) {
            console.error('Failed to load investors:', error);
        }
        return [];
    };

    const loadPortfolio = async (investorsList) => {
        const list = investorsList ?? investors;
        if (!list.length) {
            setInvestments([]);
            setStartups([]);
            return;
        }
        try {
            const allInvestments = [];
            const startupMap = new Map();
            for (const investor of list) {
                try {
                    const [portfolioRes, investmentsRes] = await Promise.all([
                        fetch(getApiUrl(`investments/investor/${investor.id}/portfolio`), { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }),
                        fetch(getApiUrl(`investments/investor/${investor.id}`), { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } })
                    ]);
                    if (portfolioRes.ok) {
                        const portfolio = await portfolioRes.json();
                        portfolio.forEach((startup) => startupMap.set(startup.id, startup));
                    }
                    if (investmentsRes.ok) {
                        const invData = await investmentsRes.json();
                        (invData || []).forEach((inv) => allInvestments.push({ ...inv, investorId: investor.id }));
                    }
                } catch (err) {
                    console.error(`Failed to load data for investor ${investor.id}:`, err);
                }
            }
            try {
                const linkedRes = await fetch(getApiUrl('users/my-network/linked-startups'), { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
                if (linkedRes.ok) {
                    const linked = await linkedRes.json();
                    (linked || []).forEach((s) => startupMap.set(s.id, s));
                }
            } catch (err) {
                console.error('Failed to load linked startups:', err);
            }
            setStartups(Array.from(startupMap.values()));
            setInvestments(allInvestments);
        } catch (error) {
            console.error('Failed to load portfolio:', error);
        }
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const list = await loadInvestors();
            await loadPortfolio(list);
        } catch (error) {
            console.error('Failed to load investors page data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!user?.id) return;
        if (isAdminMode && !effectiveNetworkId) return;
        loadData();
    }, [user?.id, isAdminMode, effectiveNetworkId]);

    const searchInvestorByEmail = async () => {
        const email = (investorSearchEmail || '').trim();
        if (!email) {
            setAlert({ isOpen: true, title: 'Validation', message: 'Please enter an email address.', type: 'info' });
            return;
        }
        setInvestorSearchLoading(true);
        setInvestorSearchResult(null);
        try {
            const response = await fetch(getApiUrl(`users/my-network/search-investor?email=${encodeURIComponent(email)}`), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const data = await response.json();
            if (response.ok) {
                setInvestorSearchResult(data);
            } else {
                setAlert({ isOpen: true, title: 'Search failed', message: data.error || data.message || 'Could not search investor.', type: 'error' });
            }
        } catch (error) {
            console.error('Investor search failed:', error);
            setAlert({ isOpen: true, title: 'Error', message: 'Search failed. Please try again.', type: 'error' });
        } finally {
            setInvestorSearchLoading(false);
        }
    };

    const linkInvestorToNetwork = async (investorId) => {
        try {
            const response = await fetch(getApiUrl('users/my-network/link-investor'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                body: JSON.stringify({ investorId })
            });
            const data = await response.json();
            if (response.ok) {
                setInvestorSearchResult(null);
                setInvestorSearchEmail('');
                loadData();
                setAlert({ isOpen: true, title: 'Success', message: data.message || 'Investor linked to your network.', type: 'success' });
            } else {
                setAlert({ isOpen: true, title: 'Error', message: data.error || data.message || 'Failed to link investor.', type: 'error' });
            }
        } catch (error) {
            console.error('Link investor failed:', error);
            setAlert({ isOpen: true, title: 'Error', message: 'Failed to link investor. Please try again.', type: 'error' });
        }
    };

    const formatCurrency = (amount) => {
        if (!amount) return '0';
        if (amount >= 10000000) return `${(amount / 10000000).toFixed(2)} Cr`;
        if (amount >= 100000) return `${(amount / 100000).toFixed(2)} L`;
        return amount.toLocaleString('en-IN');
    };

    const loadNetworkLogo = async () => {
        if (!user?.id) return;
        setNetworkLogoLoading(true);
        setNetworkLogoUrl((prev) => {
            if (prev && prev.startsWith('blob:')) window.URL.revokeObjectURL(prev);
            return null;
        });
        try {
            const list = await fileAPI.getFilesByModule('INVESTOR_NETWORK', user.id);
            const logoFile = (list || []).find((f) => (f.documentType || f.fileCategory || '').toUpperCase() === 'LOGO');
            if (logoFile?.id) {
                const { previewUrl } = await fileAPI.getFilePreview(logoFile.id);
                setNetworkLogoUrl(previewUrl);
            }
        } catch (err) {
            console.error('Failed to load network logo:', err);
        } finally {
            setNetworkLogoLoading(false);
        }
    };

    const handleLogoUpload = async (e) => {
        const file = e?.target?.files?.[0];
        if (!file || !user?.id) return;
        e.target.value = '';
        if (!file.type.startsWith('image/')) {
            setAlert({ isOpen: true, title: 'Invalid file', message: 'Please select an image file (e.g. PNG, JPG).', type: 'error' });
            return;
        }
        setLogoUploading(true);
        try {
            const list = await fileAPI.getFilesByModule('INVESTOR_NETWORK', user.id);
            const existingLogos = (list || []).filter((f) => (f.documentType || f.fileCategory || '').toUpperCase() === 'LOGO');
            for (const f of existingLogos) {
                try {
                    await fileAPI.deleteFile(f.id);
                } catch (_) {}
            }
            const formData = new FormData();
            formData.append('file', file);
            formData.append('moduleType', 'INVESTOR_NETWORK');
            formData.append('moduleId', String(user.id));
            formData.append('fileCategory', 'LOGO');
            formData.append('visibility', 'PUBLIC');
            const res = await fetch(getApiUrl('files/upload'), {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                body: formData
            });
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.message || 'Upload failed');
            }
            await loadNetworkLogo();
            setAlert({ isOpen: true, title: 'Success', message: 'Logo updated successfully.', type: 'success' });
        } catch (err) {
            setAlert({ isOpen: true, title: 'Error', message: err.message || 'Failed to upload logo', type: 'error' });
        } finally {
            setLogoUploading(false);
        }
    };

    useEffect(() => {
        if (user?.id) loadNetworkLogo();
        return () => {
            setNetworkLogoUrl((prev) => {
                if (prev && prev.startsWith('blob:')) window.URL.revokeObjectURL(prev);
                return null;
            });
        };
    }, [user?.id]);

    const navItems = [
        { id: 'portfolio', label: 'Portfolio', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4', path: `/${tenantPath}/investor-network` },
        { id: 'startups', label: 'Startups', icon: 'M13 10V3L4 14h7v7l9-11h-7z', path: `/${tenantPath}/investor-network/startups` },
        { id: 'investors', label: 'Investors', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z', path: `/${tenantPath}/investor-network/investors` },
        { id: 'users', label: 'Management', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z', path: `/${tenantPath}/investor-network/users` },
        { id: 'documents', label: 'Documents', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', path: `/${tenantPath}/investor-network/documents` },
        { id: 'activity', label: 'Activity', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', path: `/${tenantPath}/investor-network/activity` }
    ];

    if (loading && !(isAdminMode && !effectiveNetworkId)) {
        return (
            <div className="min-h-screen bg-slate-50/80 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent mx-auto" />
                    <p className="mt-4 text-gray-600 font-medium">Loading investors...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50/80">
            {/* Sidebar - same as dashboard */}
            <aside
                className={`fixed left-0 top-0 h-full z-40 flex flex-col bg-white/95 backdrop-blur-sm border-r border-slate-200/80 shadow-sm transition-[width] duration-200 ease-out ${sidebarOpen ? 'w-52' : 'w-[72px]'}`}
            >
                <div className="h-14 flex items-center shrink-0 px-3 border-b border-slate-100">
                    {sidebarOpen ? (
                        <Logo width={36} height={36} showText={true} className="[&_span]:!text-lg" />
                    ) : (
                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                            <span className="text-white font-bold text-sm">i</span>
                        </div>
                    )}
                </div>
                <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
                    {navItems.map((item) => (
                        <button
                            key={item.id}
                            type="button"
                            onClick={() => navigate(item.path)}
                            title={item.label}
                            className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all duration-150 ${item.id === 'investors' ? 'bg-indigo-50 text-indigo-700 font-medium shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                        >
                            <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon} />
                            </svg>
                            {sidebarOpen && <span className="text-sm truncate">{item.label}</span>}
                        </button>
                    ))}
                </nav>
                <div className="shrink-0 px-2 py-1 border-t border-slate-100">
                    <button
                        type="button"
                        onClick={() => setSidebarExpanded((e) => !e)}
                        className="w-full flex items-center gap-3 rounded-lg px-3 py-2 text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors"
                        title={sidebarExpanded ? 'Collapse menu' : 'Expand menu'}
                    >
                        <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={sidebarExpanded ? 'M11 19l-7-7 7-7m8 14l-7-7 7-7' : 'M13 5l7 7-7 7M5 5l7 7-7 7'} />
                        </svg>
                        {sidebarOpen && <span className="text-xs font-medium">{sidebarExpanded ? 'Collapse' : 'Expand'}</span>}
                    </button>
                </div>
                <div className="shrink-0 p-2 border-t border-slate-100 bg-slate-50/50">
                    <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => logoInputRef.current?.click()}
                            disabled={logoUploading || networkLogoLoading}
                            className="flex-shrink-0 w-9 h-9 rounded-lg overflow-hidden bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center border border-violet-200/50 hover:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-400 disabled:opacity-60 transition-all"
                            title={networkLogoUrl ? 'Change logo' : 'Upload logo'}
                        >
                            {logoUploading || networkLogoLoading ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : networkLogoUrl ? (
                                <img src={networkLogoUrl} alt="Network" className="w-full h-full object-cover" />
                            ) : (
                                <span className="text-white font-bold text-sm">{(user?.organizationName || user?.name || 'N')?.charAt(0)?.toUpperCase()}</span>
                            )}
                        </button>
                        {sidebarOpen && (
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-900 truncate">{user?.organizationName || user?.name}</p>
                                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                                <button
                                    type="button"
                                    onClick={() => logoInputRef.current?.click()}
                                    disabled={logoUploading || networkLogoLoading}
                                    className="text-xs text-violet-600 hover:text-violet-800 hover:underline disabled:opacity-60"
                                >
                                    {networkLogoUrl ? 'Change' : 'Upload'} logo
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </aside>

            <main className={`transition-[margin] duration-200 ease-out ${sidebarOpen ? 'ml-52' : 'ml-[72px]'}`}>
                <header className="h-14 bg-white/80 backdrop-blur-sm border-b border-slate-200/80 flex items-center justify-between px-6 sticky top-0 z-30">
                    <h1 className="text-xl font-bold text-gray-900">Investors</h1>
                    {isAdminMode && (
                        <select
                            value={selectedNetworkId || ''}
                            onChange={(e) => setSelectedNetworkId(e.target.value ? Number(e.target.value) : null)}
                            className="px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">Select network</option>
                            {(investorNetworks || []).map((n) => (
                                <option key={n.id} value={n.id}>{n.organizationName || n.name || n.email}</option>
                            ))}
                        </select>
                    )}
                </header>

                <div className="p-6">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                        <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-500">
                                {investors.length} {investors.length === 1 ? 'member' : 'members'} in your network
                            </span>
                            <button
                                type="button"
                                onClick={() => setInvestorSearchExpanded((e) => !e)}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ${investorSearchExpanded ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                Add by email
                            </button>
                        </div>
                        <button
                            type="button"
                            onClick={() => setShowAddInvestorModal(true)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition font-medium text-sm shadow-sm hover:shadow"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                            </svg>
                            Add Investor
                        </button>
                    </div>

                    {investorSearchExpanded && (
                        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm mb-6">
                            <div className="flex flex-wrap items-center gap-2">
                                <input
                                    type="email"
                                    value={investorSearchEmail}
                                    onChange={(e) => { setInvestorSearchResult(null); setInvestorSearchEmail(e.target.value); }}
                                    onKeyDown={(e) => e.key === 'Enter' && searchInvestorByEmail()}
                                    placeholder="investor@example.com"
                                    className="flex-1 min-w-[200px] px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                />
                                <button
                                    type="button"
                                    onClick={searchInvestorByEmail}
                                    disabled={investorSearchLoading}
                                    className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                                >
                                    {investorSearchLoading ? (
                                        <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                    ) : (
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                    )}
                                    Search
                                </button>
                            </div>
                            {investorSearchResult && (
                                <div className="mt-3 p-3 rounded-lg bg-gray-50 border border-gray-100 text-sm">
                                    {!investorSearchResult.found ? (
                                        <div className="flex flex-wrap items-center justify-between gap-3">
                                            <p className="text-gray-700">No user found. Create and link to network.</p>
                                            <button
                                                type="button"
                                                onClick={() => { setPendingCreateEmail(investorSearchEmail); setShowAddInvestorModal(true); }}
                                                className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"
                                            >
                                                Create & link
                                            </button>
                                        </div>
                                    ) : investorSearchResult.alreadyInNetwork ? (
                                        <p className="text-green-700 font-medium">Already in your network.</p>
                                    ) : !investorSearchResult.isInvestor ? (
                                        <p className="text-amber-700">User exists but is not an investor.</p>
                                    ) : (
                                        <div className="flex flex-wrap items-center justify-between gap-3">
                                            <span className="font-medium text-gray-900">{investorSearchResult.user?.name}</span>
                                            <button
                                                type="button"
                                                onClick={() => linkInvestorToNetwork(investorSearchResult.user.id)}
                                                className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"
                                            >
                                                Link to network
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {investors.length === 0 ? (
                        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center shadow-sm">
                            <div className="w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">No investors yet</h3>
                            <p className="text-gray-500 mb-6 max-w-sm mx-auto">Add investors to your network to collaborate on investments and track portfolios together.</p>
                            <button
                                type="button"
                                onClick={() => setShowAddInvestorModal(true)}
                                className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition font-medium shadow-sm"
                            >
                                Add investor
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {investors.map((investor) => {
                                const investorInvestments = investments.filter((inv) => inv.investorId === investor.id);
                                return (
                                    <button
                                        key={investor.id}
                                        type="button"
                                        onClick={() => navigate(`/${tenantPath}/investor-network/investors/${investor.id}`)}
                                        className="group bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-md hover:border-indigo-200 hover:shadow-indigo-500/5 transition-all duration-200 text-left cursor-pointer w-full"
                                    >
                                        <div className="flex items-start gap-4">
                                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0 shadow-md group-hover:shadow-lg group-hover:scale-105 transition-transform">
                                                <span className="text-white font-bold text-lg">{investor.name?.charAt(0)?.toUpperCase()}</span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-semibold text-gray-900 truncate group-hover:text-indigo-600 transition-colors">{investor.name}</h3>
                                                <p className="text-sm text-gray-500 truncate mt-0.5">{investor.email}</p>
                                                {investor.jobTitle && <p className="text-xs text-gray-400 mt-1">{investor.jobTitle}</p>}
                                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                                    {investorInvestments.length > 0 && (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-indigo-50 text-indigo-700">
                                                            {investorInvestments.length} investment{investorInvestments.length !== 1 ? 's' : ''}
                                                        </span>
                                                    )}
                                                    {investor.networkRole && (
                                                        <span className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium ${investor.networkRole === 'CEO' ? 'bg-purple-50 text-purple-700' : investor.networkRole === 'ADMIN' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                                                            {investor.networkRole}
                                                        </span>
                                                    )}
                                                </div>
                                                {investorInvestments.length > 0 && (
                                                    <p className="text-xs text-indigo-600 font-medium mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">View startups →</p>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </main>

            <CreateInvestorModal
                isOpen={showAddInvestorModal}
                onClose={() => { setShowAddInvestorModal(false); setPendingCreateEmail(''); }}
                onSuccess={() => {
                    setShowAddInvestorModal(false);
                    setPendingCreateEmail('');
                    setInvestorSearchResult(null);
                    setInvestorSearchEmail('');
                    loadData();
                    setAlert({ isOpen: true, title: 'Success', message: 'Investor added successfully!', type: 'success' });
                }}
                initialType="INDIVIDUAL"
                initialData={{ investorNetworkId: user?.id, email: pendingCreateEmail || undefined }}
                disableNetworkType={true}
            />


            <AlertDialog
                isOpen={alert.isOpen}
                onClose={() => setAlert({ ...alert, isOpen: false })}
                title={alert.title}
                message={alert.message}
                type={alert.type}
            />
        </div>
    );
};

export default InvestorNetworkInvestorsPage;
